<template lang="pug">
h5(v-if="subscriptionId")
  span.text-muted-foreground.font-normal App Store 订阅：
  b.store-release-status {{ statusText }}
  Sheet(v-model:open="sheetOpen")
    SheetTrigger(as-child)
      Button.ml-2(
        type="button"
        variant="link"
        size="sm"
        class="h-auto px-1 py-0 align-baseline text-xs"
      ) {{ released ? '撤销解绑' : '解绑' }}

    SheetContent(
      side="right"
      class="flex h-full w-[min(94vw,520px)] flex-col gap-0 overflow-hidden p-0 sm:max-w-[520px]"
    )
      SheetHeader(class="shrink-0 border-b px-5 py-4 pr-12 sm:px-7")
        SheetTitle {{ released ? '撤销解绑 App Store 订阅' : '解绑 App Store 订阅' }}

      form(class="flex min-h-0 flex-1 flex-col" @submit.prevent="submit")
        div(class="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-5 sm:px-7")
          SheetDescription.store-release-consequences(as-child class="text-destructive")
            div
              p(v-if="released") 撤销后，这个订阅重新归 App Store 管理：在它到期前，网站、余额、组织和余额自动续费都不能再买或换这组套餐。已经在其他渠道买的时长不受影响。
              ul(v-else class="list-disc space-y-1.5 pl-4")
                li 解绑后，这个订阅不再锁定该组：网站、余额、组织和余额自动续费都能买或换这组套餐。
                li 解绑不会取消订阅，也不会退款。Apple 会继续按期扣款，每次续费照样给服务加时长；如果网站上已经换了套餐，续费按月费折算。
                li 客户必须自己在 iPhone「设置 → Apple 账户 → 订阅」（或 apps.apple.com/account/subscriptions）取消 PandaFan 订阅，否则会被 Apple 和我们两边收费。要退 Apple 的钱，客户需向 Apple 申请（reportaproblem.apple.com）。
                li 只对当前这个订阅有效：订阅到期、宽限期结束或被退款时自动解除；客户以后在 App 里重新订阅，会重新归 App Store 管理。
          div.store-release-guide.text-sm.text-muted-foreground.space-y-1(v-if="!released")
            p.font-medium 什么时候解绑
            ul(class="list-disc space-y-1 pl-4")
              li 客户已经在 Apple 取消了自动续订，当前周期还没结束，但想现在就在网站、余额或组织里买或换这组套餐。
              li 不需要解绑：客户只想退款（让客户找 Apple）；客户还没在 Apple 取消订阅（先让客户取消，否则会两边扣款）。
          section.rounded-lg.border.p-4.space-y-2(class="bg-muted/30")
            div.flex.items-center.justify-between.gap-3
              p.text-sm.font-semibold {{ statusText }}
              span.rounded-full.border.px-2.py-1.font-mono.text-xs.text-muted-foreground {{ subscriptionId }}

          div.space-y-2
            Label(:for="fieldId('reason')") 原因
            Textarea(
              :id="fieldId('reason')"
              v-model="reason"
              rows="3"
              maxlength="500"
            )

          section.space-y-2
            p.text-xs.text-muted-foreground 记录
            p.text-sm.text-destructive(v-if="logError") {{ logError }}
            p.text-sm.text-muted-foreground(v-else-if="logLoading") 加载中…
            p.text-sm.text-muted-foreground(v-else-if="!releaseLog.length") 暂无解绑记录
            ul.space-y-2.text-sm(v-else)
              li.rounded-md.border.px-3.py-2(v-for="entry in releaseLog" :key="`${entry.action}-${entry.created_at}`")
                div.flex.flex-wrap.items-baseline.gap-x-2
                  b {{ entry.action === 'release' ? '解绑' : '撤销' }}
                  span.text-xs.text-muted-foreground {{ beijingTime(entry.created_at) }} · 管理员 {{ entry.admin_user_id }}
                  span.font-mono.text-xs.text-muted-foreground(v-if="entry.original_transaction_id !== subscriptionId") {{ entry.original_transaction_id }}
                p.mt-1.break-words {{ entry.reason }}

        SheetFooter(class="shrink-0 border-t px-5 py-4 sm:px-7")
          Button(type="button" variant="outline" :disabled="saving" @click="sheetOpen = false") 取消
          Button(type="submit" :variant="released ? 'default' : 'destructive'" :disabled="saving || !reason.trim()")
            Icon(v-if="saving" name="mdi:loading" class="mr-1.5 h-4 w-4 animate-spin")
            | {{ released ? '确认撤销' : '确认解绑' }}
  Button.store-audit-link(
    as="a"
    :href="'/admin?view=appstore&tab=audit&range=90d&user_id=' + service.user_id"
    target="_blank"
    variant="link"
    size="sm"
    class="h-auto px-1 py-0 align-baseline text-xs"
  ) 审计记录
</template>

<script lang="ts" setup>
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { Textarea } from '@/components/ui/textarea'
import { formatStoreExpiry, storeManagement } from '@/utils/storeManaged'
import { toast } from '~/utils/sonner'

// Admin release of a store subscription (docs/appstore-iap.md, 管理员解绑).
const RELEASE_PATH = 'admin/appstore/release'
const UNDO_PATH = 'admin/appstore/release/undo'
const LOG_PATH = 'admin/appstore/transactions'
const releaseErrors: Record<string, string> = {
  notValid: '原因必填，最多 500 字',
  notFound: '这不是该服务当前的订阅，请刷新后再试',
  illegalOperation: '解绑状态已经变化，请刷新后再试',
}

interface ReleaseLogEntry {
  action: 'release' | 'undo'
  original_transaction_id: string
  admin_user_id: number
  reason: string
  created_at: string
}

const props = defineProps<{ service: userServicesAttributes }>()
const serviceStore = useUserService()
const $utils = useUtils()
const dayjs = useDayjs()
const compId = useId()
const fieldId = (name: string) => `${compId}-${name}`

const sheetOpen = ref(false)
const reason = ref('')
const saving = ref(false)
const releaseLog = ref<ReleaseLogEntry[]>([])
const logLoading = ref(false)
const logError = ref('')

const subscriptionId = computed(() => props.service?.appstore?.original_transaction_id)
const released = computed(() => Boolean(props.service?.appstore?.release))
const statusText = computed(() => {
  const release = props.service?.appstore?.release
  if (release) return `已解绑：${release.reason}`
  const managed = storeManagement(props.service)
  if (managed) {
    const date = formatStoreExpiry(managed.expiresAt, 'zh')
    return date ? `归苹果 App Store 管理，到期 ${date}` : '归苹果 App Store 管理'
  }
  return '已到期，不再归 App Store 管理'
})

const beijingTime = (time: string) => dayjs(time).tz('Asia/Shanghai').format('YYYY-MM-DD HH:mm')

watch(sheetOpen, (open) => {
  if (!open) return
  reason.value = ''
  loadReleaseLog()
})

async function loadReleaseLog() {
  logLoading.value = true
  logError.value = ''
  try {
    const data = await useAPI().get(LOG_PATH, { params: { user_id: props.service.user_id } }) as { releases: ReleaseLogEntry[] }
    releaseLog.value = [...data.releases].reverse()
  } catch (error) {
    logError.value = `记录加载失败：${$utils.apiErrorText(error)}`
  } finally {
    logLoading.value = false
  }
}

async function submit() {
  if (!subscriptionId.value || !reason.value.trim()) return
  const undo = released.value
  saving.value = true
  try {
    await useAPI().post(undo ? UNDO_PATH : RELEASE_PATH, {
      body: { original_transaction_id: subscriptionId.value, reason: reason.value.trim() },
    })
    await serviceStore.getCurrentUserService({ refresh: true })
    toast({ title: undo ? '已撤销解绑' : '已解绑' })
    sheetOpen.value = false
  } catch (error: any) {
    toast({
      title: undo ? '撤销解绑失败' : '解绑失败',
      description: releaseErrors[error?.data?.message] || $utils.apiErrorText(error),
      variant: 'destructive',
    })
  } finally {
    saving.value = false
  }
}
</script>
